import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  Fade,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { companyManagementUrl, PULL_TRANSACTIONS_URL, REGISTER_PULL_URL } from "../../../../config";
import { fetchJsonDynamic } from "../../../../data/DynamicServiceNew/jsondynamicservice";

// ── Correct endpoints ──────────────────────────────────────────────────────────


// Roles that are NOT allowed to select branch (roleGroup in [4,5])
const RESTRICTED_ROLE_GROUPS = [4, 5];

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString("en-KE", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (amount) => {
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(parseFloat(amount));
};

const PullTransactionsTable = () => {
  // ── Token / user context ──────────────────────────────────────────────
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const tokenRegionId = parseInt(decodedToken.RegionId);
  const tokenBranchId = parseInt(decodedToken.BranchId);
  const tokenRoleGroup = parseInt(decodedToken.RoleGroup ?? decodedToken.roleGroup ?? 0);

  const canSelectBranch = !RESTRICTED_ROLE_GROUPS.includes(tokenRoleGroup);

  // ── State ─────────────────────────────────────────────────────────────
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(
    canSelectBranch ? "" : String(tokenBranchId)
  );
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalRecords, setTotalRecords] = useState(null);

  // ── Registration Modal State ──
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [registerBranchId, setRegisterBranchId] = useState(
    canSelectBranch ? "" : String(tokenBranchId)
  );
  const [nominatedNumber, setNominatedNumber] = useState("");
  const [callBackURL, setCallBackURL] = useState("https://api.sababisha.com/incomingc2b");
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState(null);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // ── Fetch branches ────────────────────────────────────────────────────
  useEffect(() => {
    if (!canSelectBranch) return;

    const fetchBranches = async () => {
      setBranchesLoading(true);
      try {
        const businessDetails = await fetchJsonDynamic(
          "allBranchesByColumnValues",
          "branchModel",
          { regionId: tokenRegionId },
          companyManagementUrl.uri
        );
        const allBranchDetails = JSON.parse(businessDetails.results) || [];
        const branchOptions = allBranchDetails.map((b) => ({
          value: b.Id,
          label: b.Name,
        }));
        setBranches(branchOptions);
      } catch (err) {
        console.error("Error fetching branches:", err);
        setError("Failed to load branches. Please refresh.");
      } finally {
        setBranchesLoading(false);
      }
    };

    fetchBranches();
  }, [tokenRegionId, canSelectBranch]);

  // ── Fetch transactions ────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!selectedBranch || !startDate || !endDate) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);
    setTransactions([]);
    setTotalRecords(null);

    try {
      const payload = {
        branchId: parseInt(selectedBranch),
        startDate: `${startDate} 00:00:00`,
        endDate: `${endDate} 23:59:59`,
        offSetValue: "0",
      };

      const token = localStorage.getItem("token");
      const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;

      const response = await fetch(PULL_TRANSACTIONS_URL.uri, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: formattedToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.responseCode === "1000" && Array.isArray(data.response)) {
        const flat = data.response.flat();
        setTransactions(flat);

        if (data.rawResponse) {
          try {
            const raw = JSON.parse(data.rawResponse.trim());
            if (raw.TotalRecords !== undefined) {
              setTotalRecords(raw.TotalRecords);
            }
          } catch {
            // rawResponse parse failed — not critical
          }
        }

        if (flat.length === 0) {
          setError("No transactions found for the selected criteria.");
        }
      } else {
        setError(data.responseMessage || "No records found.");
        setTransactions([]);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to fetch transactions. Please try again.");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Register Pull Transaction ─────────────────────────────────────────
  const handleRegisterSubmit = async () => {
    // Validate form
    if (!registerBranchId) {
      setRegisterError("Please select a branch");
      return;
    }
    if (!nominatedNumber || nominatedNumber.trim() === "") {
      setRegisterError("Please enter a nominated number");
      return;
    }
    // Basic phone number validation (Kenyan format)
    const phoneRegex = /^(07|01|\+254)\d{8,9}$/;
    if (!phoneRegex.test(nominatedNumber.trim())) {
      setRegisterError("Please enter a valid phone number (e.g., 0712345678)");
      return;
    }
    if (!callBackURL || callBackURL.trim() === "") {
      setRegisterError("Please enter a callback URL");
      return;
    }

    setRegisterLoading(true);
    setRegisterError(null);
    setRegisterSuccess(false);

    try {
      const payload = {
        branchId: parseInt(registerBranchId),
        nominatedNumber: nominatedNumber.trim(),
        callBackURL: callBackURL.trim(),
      };

      const token = localStorage.getItem("token");
      const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;

      const response = await fetch(REGISTER_PULL_URL.uri, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: formattedToken,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && (data.responseCode === "1000" || data.success)) {
        setRegisterSuccess(true);
        setSnackbarMessage("Pull transaction registered successfully!");
        setSnackbarOpen(true);
        // Reset form on success
        if (canSelectBranch) {
          setRegisterBranchId("");
        } else {
          setRegisterBranchId(String(tokenBranchId));
        }
        setNominatedNumber("");
        setCallBackURL("https://api.sababisha.com/PullCallback");
        // Close modal after short delay
        setTimeout(() => {
          setRegisterModalOpen(false);
          setRegisterSuccess(false);
        }, 1500);
      } else {
        throw new Error(data.responseMessage || data.message || "Registration failed");
      }
    } catch (err) {
      console.error("Registration error:", err);
      setRegisterError(err.message || "Failed to register pull transaction. Please try again.");
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (!registerLoading) {
      setRegisterModalOpen(false);
      setRegisterError(null);
      setRegisterSuccess(false);
      // Reset form fields
      if (canSelectBranch) {
        setRegisterBranchId("");
      } else {
        setRegisterBranchId(String(tokenBranchId));
      }
      setNominatedNumber("");
      setCallBackURL("https://api.sababisha.com/incomingc2b");
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const isReady = selectedBranch && startDate && endDate;

  // ── UI ────────────────────────────────────────────────────────────────
  return (
    <Box m="20px">
      {/* ── Header ── */}
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <AccountBalanceIcon sx={{ fontSize: 32, color: "primary.main" }} />
        <Box>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
            Pull Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Query and reconcile incoming payment transactions
          </Typography>
        </Box>
      </Box>

      {/* ── Filter Panel ── */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 3,
          mb: 3,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Search Parameters
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setRegisterModalOpen(true)}
            size="small"
          >
            Register Pull Transaction
          </Button>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr auto" }}
          gap={2}
          alignItems="flex-end"
        >
          {/* Branch selector — only for non-restricted roles */}
          {canSelectBranch ? (
            <FormControl size="small" fullWidth>
              <InputLabel>Branch *</InputLabel>
              <Select
                value={selectedBranch}
                label="Branch *"
                onChange={(e) => setSelectedBranch(e.target.value)}
                disabled={branchesLoading}
              >
                {branchesLoading ? (
                  <MenuItem disabled>Loading…</MenuItem>
                ) : (
                  branches.map((b) => (
                    <MenuItem key={b.value} value={b.value}>
                      {b.label}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              label="Branch"
              value={`Branch ID: ${tokenBranchId}`}
              disabled
              fullWidth
              helperText="Your assigned branch"
            />
          )}

          {/* Start date */}
          <TextField
            size="small"
            label="Start Date *"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          {/* End date */}
          <TextField
            size="small"
            label="End Date *"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate }}
            fullWidth
          />

          {/* Search button */}
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={!isReady || loading}
            sx={{ whiteSpace: "nowrap", height: 40 }}
          >
            {loading ? "Searching…" : "Fetch Transactions"}
          </Button>
        </Box>

        {!isReady && !hasSearched && (
          <Alert severity="info" sx={{ mt: 2 }} icon={false}>
            Select a <strong>branch</strong>, <strong>start date</strong>, and{" "}
            <strong>end date</strong> above, then click <em>Fetch Transactions</em> to load results.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* ── Results Table (hidden until first search) ── */}
      <Collapse in={hasSearched} timeout={400}>
        <Fade in={hasSearched} timeout={500}>
          <Paper
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
          >
            {/* Table header bar */}
            <Box
              px={3}
              py={2}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              borderBottom="1px solid"
              borderColor="divider"
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Results
              </Typography>
              {transactions.length > 0 && (
                <Box display="flex" gap={1} alignItems="center">
                  <Chip
                    label={`Top ${Math.min(10, transactions.length)} shown`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={`${totalRecords ?? transactions.length} total`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              )}
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : transactions.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Typography color="text.secondary">
                  No transactions found for the selected criteria.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[
                        "#",
                        "Transaction ID",
                        "Date & Time",
                        "MSISDN",
                        "Sender",
                        "Type",
                        "Bill Reference",
                        "Amount",
                        "Organization",
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{ fontWeight: 700, whiteSpace: "nowrap", bgcolor: "background.paper" }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.slice(0, 10).map((txn, idx) => (
                      <TableRow
                        key={txn.transactionId ?? idx}
                        hover
                        sx={{ "&:last-child td": { border: 0 } }}
                      >
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            fontWeight={600}
                            color="primary.main"
                          >
                            {txn.transactionId}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatDate(txn.trxDate)}
                        </TableCell>
                        <TableCell>{txn.msisdn}</TableCell>
                        <TableCell>
                          <Chip label={txn.sender} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              bgcolor: "action.hover",
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              display: "inline-block",
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                            }}
                          >
                            {txn.transactionType}
                          </Typography>
                        </TableCell>
                        <TableCell>{txn.billReference}</TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, color: "success.main", whiteSpace: "nowrap" }}
                        >
                          {formatAmount(txn.amount)}
                        </TableCell>
                        <TableCell>{txn.organizationName}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Fade>
      </Collapse>

      {/* ── Registration Modal ── */}
      <Dialog open={registerModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={600}>
              Register Pull Transaction
            </Typography>
            <IconButton onClick={handleCloseModal} disabled={registerLoading} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Branch selection - same logic as main page */}
            {canSelectBranch ? (
              <FormControl size="small" fullWidth>
                <InputLabel>Branch *</InputLabel>
                <Select
                  value={registerBranchId}
                  label="Branch *"
                  onChange={(e) => setRegisterBranchId(e.target.value)}
                  disabled={branchesLoading || registerLoading}
                >
                  {branchesLoading ? (
                    <MenuItem disabled>Loading branches...</MenuItem>
                  ) : (
                    branches.map((b) => (
                      <MenuItem key={b.value} value={b.value}>
                        {b.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            ) : (
              <TextField
                size="small"
                label="Branch"
                value={`Branch ID: ${tokenBranchId}`}
                disabled
                fullWidth
                helperText="Your assigned branch (cannot be changed)"
              />
            )}

            {/* Nominated Number */}
            <TextField
              size="small"
              label="Nominated Number *"
              placeholder="e.g., 0712345678"
              value={nominatedNumber}
              onChange={(e) => setNominatedNumber(e.target.value)}
              disabled={registerLoading}
              fullWidth
              helperText="Enter the phone number to be nominated"
            />

            {/* Callback URL - prefilled with default */}
            <TextField
              size="small"
              label="Callback URL *"
              value={callBackURL}
              onChange={(e) => setCallBackURL(e.target.value)}
              disabled={registerLoading}
              fullWidth
              helperText="URL to receive transaction callbacks"
            />

            {registerError && (
              <Alert severity="error" onClose={() => setRegisterError(null)}>
                {registerError}
              </Alert>
            )}

            {registerSuccess && (
              <Alert severity="success">Registration successful! Redirecting...</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={handleCloseModal} disabled={registerLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRegisterSubmit}
            disabled={registerLoading}
            startIcon={registerLoading ? <CircularProgress size={16} /> : null}
          >
            {registerLoading ? "Registering..." : "Register"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PullTransactionsTable;